import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import InlinePlatformNoticeModal from "@/components/InlinePlatformNoticeModal";

/* ================================================================
   Sprint I-6 — Platform Notice backfill acknowledgment (authed).
   Wraps the shared InlinePlatformNoticeModal.
   Re-prompts on:
     - first login after user was created before ack was required
     - any bump to PLATFORM_NOTICE_VERSION (server returns
       `acknowledged: false` when version drifts)
   ================================================================ */

const LOCAL_DISMISS_KEY = "atlas_platform_notice_dismissed_at";
const LOCAL_ACK_VERSION_KEY = "atlas_platform_notice_ack_version";

export default function PlatformNoticeAcknowledgment() {
    const [visible, setVisible] = useState(false);
    const [status, setStatus]   = useState(null);
    const [notice, setNotice]   = useState(null);

    useEffect(() => {
        Promise.all([
            api.get("/legal/platform-notice-status"),
            api.get("/legal/disclaimers"),
        ]).then(([sRes, dRes]) => {
            const s = sRes.data;
            const d = dRes.data;
            setStatus(s);
            setNotice(d.platform_notice);
            if (!s.acknowledged) {
                const dismissedAt = localStorage.getItem(LOCAL_DISMISS_KEY);
                const oneHourAgo = Date.now() - 60 * 60 * 1000;
                const localAckVersion = localStorage.getItem(LOCAL_ACK_VERSION_KEY);
                if (localAckVersion === s.current_version) return;
                if (!dismissedAt || parseInt(dismissedAt, 10) < oneHourAgo) {
                    setVisible(true);
                }
            } else {
                localStorage.setItem(LOCAL_ACK_VERSION_KEY, s.current_version);
            }
        }).catch(() => {});
    }, []);

    const dismissForNow = () => {
        localStorage.setItem(LOCAL_DISMISS_KEY, String(Date.now()));
        setVisible(false);
    };

    const acknowledge = async () => {
        try {
            const r = await api.post("/legal/platform-notice-acknowledge");
            toast.success("Platform Notice acknowledged. Thank you.");
            localStorage.setItem(LOCAL_DISMISS_KEY, String(Date.now()));
            localStorage.setItem(LOCAL_ACK_VERSION_KEY, r.data?.version || status?.current_version || "");
            setVisible(false);
        } catch (e) {
            toast.error("Failed to record acknowledgment. Please retry.");
        }
    };

    return (
        <InlinePlatformNoticeModal
            open={visible && !!notice}
            notice={notice}
            dismissible={true}
            subtitle="You've been signed in — please acknowledge the current Nexus One Platform Notice before continuing."
            onDismiss={dismissForNow}
            onAcknowledge={acknowledge}
        />
    );
}
